import React, { useState, useEffect } from "react";
import { Loader2, GanttChartSquare, Activity, AlertTriangle } from "lucide-react";
import {
  RadialBarChart,
  RadialBar,
  ResponsiveContainer,
  PolarAngleAxis,
} from "recharts";
import { api } from "@/lib/api";
import SectionHeader from "@/components/SectionHeader";
import { useActiveProject } from "@/lib/ActiveProjectContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";

export default function ProjectAI() {
  const { project } = useActiveProject();
  const [name, setName] = useState("");
  const [desc, setDesc] = useState(
    "EPC scope: PV modules supply, civil, mechanical, electrical and commissioning. 7 GWh annual generation. Engineer is currently 8 weeks late on inverter foundation IFC drawings; PV module shipments delayed by Suez routing surcharge."
  );
  const [progress, setProgress] = useState(72);
  const [slip, setSlip] = useState(35);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  // Prefill form when active project changes
  useEffect(() => {
    if (!project) return;
    setName(project.name || "");
    setDesc(
      project.description ||
        (project.notes ? project.notes : "") ||
        `${project.contract_type || ""} · Client: ${project.client || ""} · Value $${project.value_usd_mn || 0} mn`
    );
    if (project.progress_percent != null) setProgress(project.progress_percent);
    if (project.schedule_slip_days != null) setSlip(project.schedule_slip_days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id]);

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/project/analyze", {
        project_name: name,
        description: desc,
        progress_percent: Number(progress) || 0,
        schedule_slip_days: Number(slip) || 0,
      });
      setData(data);
      toast.success("Project analysis complete");
    } catch (e) {
      toast.error("Analysis failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="project-ai-page">
      <SectionHeader
        overline="Module 02"
        title="Project AI"
        description="Schedule risk modelling, root-cause attribution and mitigation planning. Powered by Primavera-aware reasoning."
        testid="project-ai-header"
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-4 bg-slate-900/60 border border-slate-800 rounded-sm p-4 space-y-3 h-fit">
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-cyan-400">
            INPUT
          </div>
          <div>
            <Label className="text-xs text-slate-400">Project</Label>
            <Input
              data-testid="project-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1.5 bg-slate-950 border-slate-800 rounded-sm text-slate-100"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-400">Project description</Label>
            <Textarea
              data-testid="project-desc"
              rows={6}
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              className="mt-1.5 bg-slate-950 border-slate-800 rounded-sm text-slate-100"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-400">Progress %</Label>
              <Input
                data-testid="project-progress"
                type="number"
                value={progress}
                onChange={(e) => setProgress(e.target.value)}
                className="mt-1.5 bg-slate-950 border-slate-800 rounded-sm text-slate-100"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Slip (days)</Label>
              <Input
                data-testid="project-slip"
                type="number"
                value={slip}
                onChange={(e) => setSlip(e.target.value)}
                className="mt-1.5 bg-slate-950 border-slate-800 rounded-sm text-slate-100"
              />
            </div>
          </div>
          <Button
            onClick={run}
            disabled={loading}
            data-testid="run-project-analysis"
            className="w-full bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold rounded-sm"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Modelling delay risk…
              </>
            ) : (
              "Run Schedule Risk Analysis"
            )}
          </Button>
        </div>

        <div className="lg:col-span-8 space-y-4">
          {!data && !loading && (
            <div
              className="bg-slate-900/40 border border-dashed border-slate-800 rounded-sm p-12 text-center"
              data-testid="project-empty"
            >
              <GanttChartSquare className="h-10 w-10 text-slate-700 mx-auto mb-4" />
              <h3 className="font-display text-lg text-slate-300">
                No analysis yet
              </h3>
              <p className="text-sm text-slate-500 mt-1.5">
                Run the analysis to see delay probability, root causes, cost
                impact and mitigation strategy.
              </p>
            </div>
          )}
          {loading && (
            <div className="bg-slate-900/60 border border-slate-800 rounded-sm p-10 text-center">
              <Loader2 className="h-8 w-8 text-cyan-400 animate-spin mx-auto mb-3" />
              <div className="font-mono text-xs text-cyan-400 uppercase tracking-[0.2em]">
                Modelling…
              </div>
            </div>
          )}
          {data && <ProjectResults data={data} />}
        </div>
      </div>
    </div>
  );
}

function ProjectResults({ data }) {
  return (
    <div className="space-y-4" data-testid="project-results">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <DelayMeter value={data.delay_probability} />
        <Stat
          label="Cost impact"
          value={`$${data.cost_impact_usd_mn}`}
          unit="mn"
          icon={Activity}
        />
        <Stat
          label="Critical path"
          value={data.critical_path_impact}
          small
          icon={AlertTriangle}
        />
      </div>

      <Panel title="Root Causes">
        <ul className="grid md:grid-cols-2 gap-2 text-sm text-slate-200">
          {(data.root_causes || []).map((c, i) => (
            <li
              key={i}
              className="flex gap-2 border border-slate-800 bg-slate-950/50 px-3 py-2 rounded-sm"
            >
              <span className="text-cyan-400 font-mono text-xs">
                {String(i + 1).padStart(2, "0")}
              </span>
              {c}
            </li>
          ))}
        </ul>
      </Panel>

      <Panel title="Mitigation Plan">
        <ol className="space-y-2 list-decimal list-inside text-sm text-slate-200">
          {(data.mitigation || []).map((m, i) => (
            <li key={i}>{m}</li>
          ))}
        </ol>
      </Panel>

      <Panel title="Risk Heatmap">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {(data.heatmap || []).map((h, i) => {
            const score = (h.likelihood || 0) * (h.impact || 0);
            const color =
              score >= 16
                ? "bg-rose-500/70"
                : score >= 12
                ? "bg-amber-500/60"
                : score >= 8
                ? "bg-amber-400/30"
                : "bg-cyan-500/25";
            return (
              <div
                key={i}
                className={`heat-cell rounded-sm border border-slate-800 p-2.5 ${color}`}
                data-testid={`project-heat-${i}`}
              >
                <div className="text-[10px] font-mono text-slate-200/80">
                  L{h.likelihood} · I{h.impact} · {score}
                </div>
                <div className="text-xs font-semibold text-white mt-1">
                  {h.category}
                </div>
              </div>
            );
          })}
        </div>
      </Panel>

      {data.summary && (
        <div className="bg-slate-900/80 border-l-2 border-cyan-500 border-y border-r border-slate-800 rounded-sm p-4 text-sm text-slate-200">
          {data.summary}
        </div>
      )}
    </div>
  );
}

function DelayMeter({ value }) {
  const v = Math.max(0, Math.min(100, value || 0));
  const color = v >= 60 ? "#F43F5E" : v >= 40 ? "#F59E0B" : "#10B981";
  return (
    <div
      className="bg-slate-900/60 border border-slate-800 rounded-sm p-4"
      data-testid="delay-meter"
    >
      <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500 mb-2">
        Delay Probability
      </div>
      <div className="relative">
        <ResponsiveContainer width="100%" height={150}>
          <RadialBarChart
            cx="50%"
            cy="50%"
            innerRadius="70%"
            outerRadius="100%"
            barSize={14}
            data={[{ name: "delay", value: v }]}
            startAngle={90}
            endAngle={-270}
          >
            <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
            <RadialBar
              background={{ fill: "#1E293B" }}
              dataKey="value"
              cornerRadius={0}
              fill={color}
            />
          </RadialBarChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <div
            className="font-display text-3xl font-bold"
            style={{ color }}
          >
            {v}%
          </div>
          <div className="text-[10px] font-mono text-slate-500 uppercase tracking-wider">
            risk
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, unit, small, icon: Icon }) {
  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-sm p-4">
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">
          {label}
        </div>
        <Icon className="h-3.5 w-3.5 text-cyan-400" />
      </div>
      <div
        className={`mt-2 ${
          small ? "text-sm text-slate-200" : "font-display text-2xl font-bold text-white"
        }`}
      >
        {value} {unit && <span className="text-slate-500 text-base">{unit}</span>}
      </div>
    </div>
  );
}

function Panel({ children, title }) {
  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-sm p-4">
      <div className="font-display text-base font-semibold text-slate-50 mb-3">
        {title}
      </div>
      {children}
    </div>
  );
}
